import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Code2, 
  BrainCircuit, 
  Server, 
  Database, 
  Layers, 
  Workflow,
  Lightbulb,
  Loader2,
  Play,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { generateChallenge, executeCode } from '../services/togetherService';
import TestResults from '../components/TestResults';
import AIChatAssistant from '../components/AIChatAssistant';
import { getSelectionHint } from '../services/hintService';
import HintPopup from '../components/HintPopup';
import { SEO } from '../components/SEO';

type ChallengeType = 'csharp' | 'dotnet' | 'aspnet';
type DifficultyLevel = 'Easy' | 'Medium' | 'Hard';
type ChallengeCategory = 'algorithms' | 'data-structures' | 'design-patterns' | 'api-design' | 'database' | 'async';

interface Challenge {
  title: string;
  description: string;
  examples: {
    input: string;
    output: string;
    explanation: string;
  }[];
  difficulty: DifficultyLevel;
  hints: string[];
  testCases: TestCase[];
  functionTemplate: string;
  solutionCode: string;
}

interface TestCase {
  input: string;
  expectedOutput: string;
  isHidden?: boolean;
}

interface TestResult {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  passed: boolean;
  isHidden?: boolean;
}

export default function CodingChallenge() {
  const [selectedType, setSelectedType] = useState<ChallengeType>('csharp');
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel>('Medium');
  const [selectedCategory, setSelectedCategory] = useState<ChallengeCategory>('algorithms');
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [code, setCode] = useState('');
  const [showHints, setShowHints] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [showSelections, setShowSelections] = useState(true);
  const [generatingChallenge, setGeneratingChallenge] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [hintPosition, setHintPosition] = useState({ x: 0, y: 0 });
  const [hint, setHint] = useState('');
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const challengeTypes = [
    {
      id: 'csharp',
      title: 'C# Core',
      icon: Code2,
      description: 'Focus on C# language features and fundamentals'
    },
    {
      id: 'dotnet',
      title: '.NET Framework',
      icon: Layers,
      description: 'Practice with .NET libraries and frameworks'
    },
    {
      id: 'aspnet',
      title: 'ASP.NET',
      icon: Server,
      description: 'Web development and API challenges'
    }
  ];

  const categories = [
    {
      id: 'algorithms',
      title: 'Algorithms',
      icon: Workflow,
      description: 'Problem-solving and optimization'
    },
    {
      id: 'data-structures',
      title: 'Data Structures',
      icon: BrainCircuit,
      description: 'Implementation and usage'
    },
    {
      id: 'design-patterns',
      title: 'Design Patterns',
      icon: Layers,
      description: 'Common patterns and best practices'
    },
    {
      id: 'api-design',
      title: 'API Design',
      icon: Server,
      description: 'RESTful APIs and services'
    },
    {
      id: 'database',
      title: 'Database',
      icon: Database,
      description: 'Data access and management'
    },
    {
      id: 'async',
      title: 'Async Programming',
      icon: Workflow,
      description: 'Asynchronous operations'
    }
  ];

  const generateNewChallenge = async () => {
    setGeneratingChallenge(true);
    setLoading(true);
    try {
      const newChallenge = await generateChallenge(`${selectedCategory} in ${selectedType} at ${selectedDifficulty} level`);
      setChallenge(newChallenge);
      setCode(newChallenge.functionTemplate);
      setTestResults([]);
      setShowSelections(false);
    } catch (error) {
      console.error('Error:', error);
      if (error instanceof Error && error.message.includes('429')) {
        alert('We\'re experiencing high traffic. Please try again in a few seconds.');
      } else {
        alert('Failed to generate challenge. Please try again.');
      }
    } finally {
      setGeneratingChallenge(false);
      setLoading(false);
    }
  };

  const runTests = async () => {
    if (!challenge) return;
    
    setIsRunning(true);
    try {
      const results = await executeCode(code, challenge.testCases);
      setTestResults(results);
    } catch (error) {
      console.error('Error running tests:', error);
      alert(error instanceof Error ? error.message : 'Error running tests');
    } finally {
      setIsRunning(false);
    }
  };

  useEffect(() => {
    const handleTextSelection = async () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        return;
      }

      const text = selection.toString().trim();
      if (text.length < 3) {
        return;
      }

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setHintPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      });

      if (text !== selectedText) {
        setSelectedText(text);
        setIsHintLoading(true);
        setShowHint(true);

        const hintText = await getSelectionHint(text);
        setHint(hintText);
        setIsHintLoading(false);
      }
    };

    document.addEventListener('mouseup', handleTextSelection);
    return () => document.removeEventListener('mouseup', handleTextSelection);
  }, [selectedText]);

  const handleCloseHint = () => {
    setShowHint(false);
    setSelectedText('');
  };

  return (
    <>
      <SEO 
        title="Coding Challenge"
        description="Test your programming skills with LYFY's coding challenges. Perfect for developers looking to improve their problem-solving abilities."
        keywords="coding challenge, programming problems, developer skills, technical assessment, coding practice"
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Coding Challenges
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-300 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Practice your .NET skills with our curated challenges.
            </motion.p>
          </div>

          {/* Selection Panels Toggle */}
          {challenge && (
            <div className="mb-6">
              <button
                onClick={() => setShowSelections(!showSelections)}
                className="flex items-center justify-between w-full bg-gray-800 p-4 rounded-lg text-white hover:bg-gray-700 transition-colors"
              >
                <span className="flex items-center space-x-2">
                  <span className="font-medium">Challenge Settings</span>
                  <span className="text-sm text-gray-400">
                    {selectedType} | {selectedDifficulty} | {selectedCategory}
                  </span>
                </span>
                {showSelections ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
            </div>
          )}

          {/* Selection Panels */}
          <AnimatePresence>
            {showSelections && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                {/* Challenge Type Selection */}
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {challengeTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id as ChallengeType)}
                      className={`p-6 rounded-xl transition-all duration-300 ${
                        selectedType === type.id
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <type.icon className="w-8 h-8 mb-4 mx-auto" />
                      <h3 className="text-lg font-semibold mb-2">{type.title}</h3>
                      <p className="text-sm opacity-80">{type.description}</p>
                    </button>
                  ))}
                </motion.div>

                {/* Difficulty Selection */}
                <motion.div 
                  className="mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-6">Difficulty Level</h2>
                  <div className="flex flex-wrap gap-4">
                    {['Easy', 'Medium', 'Hard'].map((level) => (
                      <button
                        key={level}
                        onClick={() => setSelectedDifficulty(level as DifficultyLevel)}
                        className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                          selectedDifficulty === level
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </motion.div>

                {/* Category Selection */}
                <motion.div 
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id as ChallengeCategory)}
                      className={`p-6 rounded-xl transition-all duration-300 ${
                        selectedCategory === category.id
                          ? 'bg-green-600 text-white shadow-lg shadow-green-500/20'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <category.icon className="w-8 h-8 mb-4 mx-auto" />
                      <h3 className="text-lg font-semibold mb-2">{category.title}</h3>
                      <p className="text-sm opacity-80">{category.description}</p>
                    </button>
                  ))}
                </motion.div>

                {/* Generate Button */}
                <motion.div 
                  className="text-center mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <button
                    onClick={generateNewChallenge}
                    disabled={generatingChallenge}
                    className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingChallenge ? (
                      <span className="flex items-center">
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating Challenge...
                      </span>
                    ) : (
                      'Generate Challenge'
                    )}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading State */}
          {loading && !challenge && (
            <div className="flex flex-col items-center justify-center space-y-4 py-12">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-200 rounded-full animate-spin">
                  <div className="absolute top-0 right-0 w-4 h-4 bg-purple-600 rounded-full"></div>
                </div>
              </div>
              <p className="text-white text-lg">Generating your challenge...</p>
              <p className="text-gray-400 text-sm">This might take a few seconds</p>
            </div>
          )}

          {/* Challenge Content */}
          <AnimatePresence mode="wait">
            {challenge && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">{challenge.title}</h2>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                        challenge.difficulty === 'Easy' 
                          ? 'bg-green-100 text-green-800' 
                          : challenge.difficulty === 'Medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {challenge.difficulty}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="prose max-w-none mb-6">
                    <h3 className="text-lg font-semibold mb-2">Problem Statement</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-800 whitespace-pre-wrap">{challenge.description}</p>
                    </div>
                  </div>

                  {/* Examples */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Examples</h3>
                    <div className="space-y-4">
                      {challenge.examples.map((example, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="grid grid-cols-1 gap-2">
                            <div>
                              <span className="font-medium text-gray-700">Input: </span>
                              <code className="bg-gray-100 px-2 py-1 rounded">{example.input}</code>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Output: </span>
                              <code className="bg-gray-100 px-2 py-1 rounded">{example.output}</code>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Explanation: </span>
                              <span className="text-gray-600">{example.explanation}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hints */}
                  <div className="mb-6">
                    <button
                      onClick={() => setShowHints(!showHints)}
                      className="flex items-center text-purple-600 hover:text-purple-700 font-medium"
                    >
                      <Lightbulb className="w-5 h-5 mr-2" />
                      {showHints ? 'Hide Hints' : 'Show Hints'}
                    </button>
                    {showHints && (
                      <div className="mt-3 space-y-2">
                        {challenge.hints.map((hint, index) => (
                          <div key={index} className="bg-purple-50 p-3 rounded-lg text-purple-800">
                            <span className="font-medium">Hint {index + 1}: </span>
                            {hint}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Code Editor */}
                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <div className="bg-gray-800 px-4 py-2 flex justify-between items-center">
                      <span className="text-gray-300 font-medium">Solution</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={runTests}
                          disabled={isRunning}
                          className="flex items-center px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300"
                        >
                          {isRunning ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Running...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Run Tests
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="h-[400px]">
                      <Editor
                        height="100%"
                        defaultLanguage="csharp"
                        theme="vs-dark"
                        value={code}
                        onChange={(value) => setCode(value || '')}
                        options={{
                          minimap: { enabled: false },
                          fontSize: 14,
                          formatOnPaste: true,
                          formatOnType: true,
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          tabSize: 4,
                          wordWrap: 'on'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Test Results */}
                {testResults.length > 0 && (
                  <>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                      <TestResults
                        results={testResults}
                        showHidden={showHidden}
                        onToggleHidden={() => setShowHidden(!showHidden)}
                      />
                    </div>
                    <AIChatAssistant
                      code={code}
                      challenge={challenge}
                      testResults={testResults}
                    />
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {showHint && (
          <HintPopup
            hint={hint}
            position={hintPosition}
            onClose={handleCloseHint}
            isLoading={isHintLoading}
          />
        )}
      </div>
    </>
  );
}
